import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { auditApi } from "@/api/audit";
import { ApiError } from "@/api/types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatRelative } from "@/lib/format";
import { Download } from "lucide-react";

export const Route = createFileRoute("/_authed/audit")({
  component: AuditPage,
});

function AuditPage() {
  const [action, setAction] = useState("");
  const q = useQuery({
    queryKey: ["audit", action],
    queryFn: () => auditApi.list({ action: action || undefined }),
    retry: false,
  });

  // Cloud-only feature: OSS doesn't mount the route → 404.
  const unavailable = q.error instanceof ApiError && q.error.status === 404;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Security" title="Audit log" />

      {unavailable ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">
          Audit logs are a cloud feature and aren't enabled on this deployment.
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="flex items-center justify-between gap-2 border-b px-5 py-3">
            <input
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="Filter by action, e.g. member.role_changed"
              className="h-8 w-72 rounded-md border border-input bg-background px-2 text-[13px] focus:outline-none focus:ring-1 focus:ring-ring"
            />
            <a href={auditApi.exportUrl(action || undefined)}>
              <Button variant="ghost" size="sm" className="gap-1.5">
                <Download size={14} /> Export JSON
              </Button>
            </a>
          </div>

          {(q.data?.entries ?? []).length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">
              {q.isLoading ? "Loading…" : "No audit events yet"}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="pl-5 font-mono text-[11px] uppercase tracking-wider">When</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Action</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Actor</TableHead>
                    <TableHead className="font-mono text-[11px] uppercase tracking-wider">Target</TableHead>
                    <TableHead className="pr-5 font-mono text-[11px] uppercase tracking-wider">IP</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(q.data?.entries ?? []).map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="tabular pl-5 font-mono text-[13px] text-muted-foreground">{formatRelative(e.occurred_at)}</TableCell>
                      <TableCell className="font-mono text-[13px]">{e.action}</TableCell>
                      <TableCell className="text-[13px] text-muted-foreground">{e.actor_type === "system" ? "system" : "user"}</TableCell>
                      <TableCell className="font-mono text-[13px] text-muted-foreground">{e.target_type ? `${e.target_type}${e.target_id ? " " + e.target_id.slice(0, 8) : ""}` : "—"}</TableCell>
                      <TableCell className="pr-5 font-mono text-[13px] text-muted-foreground">{e.ip || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
